import {
  addMonths,
  EMPTY,
  formatRoadmapValue,
  formatShortYearMonth,
  PREFERENCE_SCHEMAS,
  type PreferenceValue,
  ProjectStatuses,
  ROADMAP_DEFAULT_MONTHS,
  ROADMAP_DEFAULT_RATE,
  ROADMAP_DEFAULT_STATUSES,
  ROADMAP_FIELDS,
  ROADMAP_UNITS,
  type Roadmap,
  routes,
  Scenarios,
  toCents,
  todayIso,
  yearMonthKey,
  yearMonthTextSchema,
} from '@pmai/contracts';
import {
  Button,
  CheckboxGroup,
  ChoiceButtons,
  EmptyState,
  PageHeader,
  Spinner,
  TextField,
  Timeline,
  type TimelineBlock,
} from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useApiQuery } from '../../shared/api/hooks';
import { useEnumLabel } from '../../shared/i18n';
import { usePreference } from '../../shared/preferences';

type Filters = PreferenceValue<'roadmapFilters'>;

/** This month + 5, Planning / Active / Active Hot, Plan revenue in EUR at 75 EUR/h (FR-OVR-013…015). */
function defaults(): Filters {
  const [year, month] = todayIso().split('-').map(Number) as [number, number];
  const from = { year, month };
  return {
    from: yearMonthKey(from),
    to: yearMonthKey(addMonths(from, ROADMAP_DEFAULT_MONTHS - 1)),
    statuses: [...ROADMAP_DEFAULT_STATUSES],
    view: 'plan',
    field: 'projectRevenue',
    unit: 'eur',
    rate: ROADMAP_DEFAULT_RATE,
  };
}

/** Roadmap (FR-OVR-013…018): planned or invoiced amounts per project and month. */
export function RoadmapPage() {
  const { t } = useTranslation();
  const pref = usePreference('roadmapFilters');
  return (
    <>
      <PageHeader title={t('roadmap.title')} />
      {pref.value === undefined ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <RoadmapView filters={pref.value ?? defaults()} save={pref.save} />
      )}
    </>
  );
}

function RoadmapView({ filters, save }: { filters: Filters; save: (next: Filters) => void }) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  // Period and statuses take effect on "Atjaunināt"; view, data, unit and rate at once (FR-OVR-017).
  const [draft, setDraft] = useState({ from: filters.from, to: filters.to, statuses: filters.statuses });
  const [rateText, setRateText] = useState(String(filters.rate));
  const [badPeriod, setBadPeriod] = useState(false);
  const set = (change: Partial<Filters>) => save({ ...filters, ...change });
  const update = () => {
    const ok =
      yearMonthTextSchema.safeParse(draft.from).success && yearMonthTextSchema.safeParse(draft.to).success;
    setBadPeriod(!ok);
    if (ok) set(draft);
  };
  const onRate = (text: string) => {
    setRateText(text);
    const rate = PREFERENCE_SCHEMAS.roadmapFilters.shape.rate.safeParse(text === '' ? NaN : Number(text));
    if (rate.success) set({ rate: rate.data });
  };

  const invalid = badPeriod || filters.from > filters.to;
  const noStatuses = filters.statuses.length === 0;
  const roadmap = useApiQuery(
    routes.overview.roadmap,
    {
      query: {
        from: filters.from,
        to: filters.to,
        statuses: filters.statuses.join(','),
        view: filters.view,
        field: filters.field,
      },
    },
    { enabled: !invalid && !noStatuses, placeholderData: (previous) => previous },
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-4 rounded-card border border-border bg-surface p-4">
        <div className="flex flex-wrap items-end gap-4">
          <TextField
            label={t('roadmap.from')}
            type="month"
            value={draft.from}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
          />
          <TextField
            label={t('roadmap.to')}
            type="month"
            value={draft.to}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
          />
          <Button icon="refresh-cw" onClick={update}>
            {t('common.update')}
          </Button>
        </div>
        <CheckboxGroup
          label={t('roadmap.statuses')}
          options={ProjectStatuses.codes.map((code) => ({
            value: code,
            label: label(ProjectStatuses.byCode(code)),
          }))}
          value={draft.statuses}
          onChange={(statuses) => setDraft((d) => ({ ...d, statuses }))}
        />
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <ChoiceButtons
            showLabel
            label={t('roadmap.view')}
            value={filters.view}
            options={Scenarios.codes.map((v) => ({ value: v, label: label(Scenarios.byCode(v)) }))}
            onChange={(view) => set({ view })}
          />
          <ChoiceButtons
            showLabel
            label={t('roadmap.field')}
            value={filters.field}
            options={ROADMAP_FIELDS.map((v) => ({ value: v, label: t(`roadmap.fields.${v}`) }))}
            onChange={(field) => set({ field })}
          />
          <ChoiceButtons
            showLabel
            label={t('roadmap.unit')}
            value={filters.unit}
            options={ROADMAP_UNITS.map((v) => ({ value: v, label: t(`roadmap.units.${v}`) }))}
            onChange={(unit) => set({ unit })}
          />
          {filters.unit === 'hours' && (
            <div className="w-36">
              <TextField
                label={t('roadmap.rate')}
                type="number"
                min={1}
                step={1}
                value={rateText}
                onChange={(e) => onRate(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      {invalid ? (
        <EmptyState icon="map" title={t('roadmap.invalidPeriod')} />
      ) : noStatuses || roadmap.data?.projects.length === 0 ? (
        <EmptyState icon="map" title={t('roadmap.noProjects')} />
      ) : roadmap.data === undefined ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <RoadmapTable roadmap={roadmap.data} unit={filters.unit} rate={filters.rate} />
      )}
    </div>
  );
}

function RoadmapTable({ roadmap, unit, rate }: { roadmap: Roadmap; unit: Filters['unit']; rate: number }) {
  const { t } = useTranslation();
  const value = (amount: string) => formatRoadmapValue(amount, unit, rate);
  return (
    <Timeline
      label={t('roadmap.title')}
      rowHeader={t('roadmap.project')}
      columns={roadmap.months.map((m) => ({
        key: yearMonthKey(m),
        label: formatShortYearMonth(m),
        sub: toCents(m.total) > 0 ? value(m.total) : EMPTY,
      }))}
      rows={roadmap.projects.map((p) => ({
        key: p.id,
        label: (
          <Link to={`/projects/${p.id}`} title={p.name} className="text-brand-700 hover:underline">
            {p.name}
          </Link>
        ),
        cells: p.values.map((v, i): TimelineBlock | null =>
          p.greyMonths
            ? p.greyMonths[i]
              ? { text: EMPTY, muted: true }
              : null
            : v === null
              ? null
              : { text: value(v) },
        ),
      }))}
    />
  );
}
